import type { AnchorHTMLAttributes, ReactNode } from "react";
import { cn } from "../../lib/cn";
import { Badge } from "../Badge";
import type { NavIcon } from "./Sidebar";

export interface BottomNavItem {
  id: string;
  label: string;
  href: string;
  icon: NavIcon;
  badge?: number | string;
}

export interface BottomNavRenderLinkArgs {
  item: BottomNavItem;
  active: boolean;
  props: AnchorHTMLAttributes<HTMLAnchorElement>;
  children: ReactNode;
}

export interface BottomNavProps {
  /** No máximo 5 (seção 11). O 5º costuma ser "Mais", que abre o drawer. */
  items: BottomNavItem[];
  activeId?: string;
  onNavigate?: (item: BottomNavItem) => void;
  renderLink?: (args: BottomNavRenderLinkArgs) => ReactNode;
  className?: string;
}

/**
 * Barra inferior — só no mobile. Item ativo em violeta (texto da marca);
 * contador numérico vira badge sólido vermelho sobre o ícone.
 * Existe porque o uso real do produto é "em pé, no celular, 15-20x por dia":
 * o que o usuário checa nesse modo tem que estar a um toque do polegar.
 */
export function BottomNav({
  items,
  activeId,
  onNavigate,
  renderLink,
  className,
}: BottomNavProps) {
  const visible = items.slice(0, 5);

  return (
    <nav
      aria-label="Navegação rápida"
      className={cn(
        "sticky bottom-0 z-30 flex h-16 shrink-0 items-stretch justify-around gap-1 border-t border-border bg-surface px-2 pt-1.5 md:hidden",
        // oxlint-disable-next-line eslint-js/no-restricted-syntax -- 0px é só o fallback de env(safe-area-inset-bottom), não medida de layout
        "box-content pb-[calc(0.375rem+env(safe-area-inset-bottom,0px))]",
        className,
      )}
    >
      {visible.map((item) => {
        const active = item.id === activeId;
        const Icon = item.icon;
        const showBadge = item.badge !== undefined && item.badge !== 0 && item.badge !== "0";

        const anchorProps: AnchorHTMLAttributes<HTMLAnchorElement> = {
          href: item.href,
          "aria-current": active ? "page" : undefined,
          onClick: () => onNavigate?.(item),
          className: cn(
            "relative flex min-h-11 flex-1 flex-col items-center justify-center gap-1 rounded-md",
            "text-2xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
            active ? "text-primary-text" : "text-subtle-foreground hover:text-foreground",
          ),
        };

        const content = (
          <>
            <span className="relative">
              <Icon aria-hidden="true" className="size-5" />
              {showBadge ? (
                <Badge
                  tone="danger"
                  solid
                  numeric
                  aria-hidden="true"
                  className="absolute -top-1.5 left-3 ring-2 ring-surface"
                >
                  {item.badge}
                </Badge>
              ) : null}
            </span>
            <span className="truncate">
              {item.label}
              {showBadge ? <span className="sr-only"> ({item.badge})</span> : null}
            </span>
          </>
        );

        return renderLink ? (
          <span key={item.id} className="contents">
            {renderLink({ item, active, props: anchorProps, children: content })}
          </span>
        ) : (
          <a key={item.id} {...anchorProps}>
            {content}
          </a>
        );
      })}
    </nav>
  );
}
